import { ChangeDetectionStrategy, Component, effect, input, output, signal } from '@angular/core';

type PresetKey = 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth' | 'custom';

@Component({
  selector: 'app-date-range-presets',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div>
      <div class="flex flex-wrap gap-2 mb-2">
        @for (preset of presets; track preset.key) {
          <button
            (click)="selectPreset(preset.key)"
            class="px-3 py-1 text-xs rounded-full border transition-colors"
            [class.bg-blue-600]="activePreset() === preset.key"
            [class.text-white]="activePreset() === preset.key"
            [class.border-blue-600]="activePreset() === preset.key"
            [class.bg-white]="activePreset() !== preset.key"
            [class.text-neutral-700]="activePreset() !== preset.key"
            [class.border-neutral-300]="activePreset() !== preset.key"
            [class.hover:bg-neutral-50]="activePreset() !== preset.key"
          >
            {{ preset.label }}
          </button>
        }
      </div>
      @if (activePreset() === 'custom') {
        <div class="flex items-center gap-3">
          <div>
            <label class="text-xs text-neutral-500 mb-1 block" i18n>From</label>
            <input
              type="date"
              [value]="dateFrom()"
              (change)="onDateFromChange($event)"
              class="form-control text-sm"
            />
          </div>
          <div>
            <label class="text-xs text-neutral-500 mb-1 block" i18n>To</label>
            <input
              type="date"
              [value]="dateTo()"
              (change)="onDateToChange($event)"
              class="form-control text-sm"
            />
          </div>
        </div>
      }
    </div>
  `,
})
export class DateRangePresetsComponent {
  readonly dateFrom = input('');
  readonly dateTo = input('');
  readonly dateRangeChanged = output<{ date_from: string; date_to: string }>();

  activePreset = signal<PresetKey | ''>('');

  presets: Array<{ key: PresetKey; label: string }> = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'last7', label: 'Last 7 days' },
    { key: 'last30', label: 'Last 30 days' },
    { key: 'thisMonth', label: 'This month' },
    { key: 'custom', label: 'Custom range' },
  ];

  constructor() {
    // Detect preset from URL-restored dates
    effect(() => {
      const from = this.dateFrom();
      const to = this.dateTo();
      if (!from && !to) {
        this.activePreset.set('');
        return;
      }
      // Only detect on first render (when activePreset is still '')
      if (this.activePreset() !== '') return;

      const detected = this.detectPreset(from, to);
      this.activePreset.set(detected);
    });
  }

  selectPreset(key: PresetKey): void {
    this.activePreset.set(key);
    const now = new Date();
    let from = '';
    let to = '';

    switch (key) {
      case 'today':
        from = to = this.formatDate(now);
        break;
      case 'yesterday': {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        from = to = this.formatDate(yesterday);
        break;
      }
      case 'last7': {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        from = this.formatDate(weekAgo);
        to = this.formatDate(now);
        break;
      }
      case 'last30': {
        const monthAgo = new Date(now);
        monthAgo.setDate(monthAgo.getDate() - 30);
        from = this.formatDate(monthAgo);
        to = this.formatDate(now);
        break;
      }
      case 'thisMonth': {
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        from = this.formatDate(firstDay);
        to = this.formatDate(now);
        break;
      }
      case 'custom':
        return; // Don't emit — wait for manual date inputs
    }

    this.dateRangeChanged.emit({ date_from: from, date_to: to });
  }

  onDateFromChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.dateRangeChanged.emit({ date_from: value, date_to: this.dateTo() });
  }

  onDateToChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.dateRangeChanged.emit({ date_from: this.dateFrom(), date_to: value });
  }

  private detectPreset(from: string, to: string): PresetKey {
    const today = this.formatDate(new Date());
    const yesterday = this.formatDate((() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; })());
    const last7 = this.formatDate((() => { const d = new Date(); d.setDate(d.getDate() - 7); return d; })());
    const last30 = this.formatDate((() => { const d = new Date(); d.setDate(d.getDate() - 30); return d; })());
    const firstOfMonth = this.formatDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

    if (from === today && to === today) return 'today';
    if (from === yesterday && to === yesterday) return 'yesterday';
    if (from === last7 && to === today) return 'last7';
    if (from === last30 && to === today) return 'last30';
    if (from === firstOfMonth && to === today) return 'thisMonth';
    return 'custom';
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }
}
